// @ts-strict-ignore
import React from 'react';
import { APP } from '../../config';
import analytics from '../../modules/analytics';
import nonCriticalException from '../../modules/exceptionLogger';

import './ExternalLink.scss';

type ExternalLinkProps = {
  className?: string;
  href: string;
  text?: string;
  color?: string;
  appearance?: string;
  trackingParams?: {
    eventName: string;
    params?: Record<string, any>;
  };
  target?: string;
  testID?: string;
};

/**
 * Used to open links in a new page outside of our website or app
 * Tracking params can be passed in in the shape:
 * {
 *   eventName: 'some_tracking_event_name',
 *   params: {
 *     additional_tracking_param: "a value"
 *   }
 * }
 * and the event will be tracked when the link is clicked.
 */
const ExternalLink: React.FCWithChildren<ExternalLinkProps> = ({
  className = '',
  href,
  text = '',
  appearance = '',
  color = undefined,
  trackingParams = undefined,
  children = undefined,
  target = '_blank',
  testID = undefined,
}) => {
  /**
   * Triggered when link is clicked. Opens
   * link using window.open with _system if
   * this is the app.
   * @param  {Object} event The click event
   * @return {undefined}
   */
  const onLinkClick = async event => {
    if (trackingParams) {
      try {
        await analytics.track(trackingParams.eventName, trackingParams.params);
      } catch (e) {
        nonCriticalException(e);
      }
    }
    if (APP) {
      event.preventDefault();
      window.open(href, '_system');
    }
  };

  return (
    <a
      href={href}
      className={(className || appearance) && `${className} ${appearance}`}
      onClick={onLinkClick}
      target={target}
      rel="noopener noreferrer"
      data-testid={testID}
      style={{
        ...(color && { color }),
      }}
    >
      {children || text}
    </a>
  );
};

export default ExternalLink;
